// src/pages/DashboardPage.tsx
import type { RawAxiosRequestHeaders } from "axios";

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Stack,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  styled,
  IconButton,
  Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PublicIcon from "@mui/icons-material/Public";     
import DomainIcon from "@mui/icons-material/Domain";     
import PersonIcon from "@mui/icons-material/Person";     
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddIcon from "@mui/icons-material/Add";

import { API_BASE } from "../services/api";
import { useNavigate } from "react-router-dom";

import FolderCreateDialog from "../components/FolderCreateDialog";
import FileUploadDialog from "../components/FileUploadDialog";
import FolderEditDialog from "../components/FolderEditDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import FolderTree from "../components/FolderTree";
import FileItemCard from "../components/FileItemCard";

import type { FolderRow } from "../types/folder";
import type { FileRow } from "../types/file";
import type { ApiResponse } from "../types/api-response";

// ... (Styled Components 기존 동일 - 생략 없이 전체 코드 요청하셨으니 포함합니다)
const SectionBox = styled(Box)(({ theme }) => ({
  flex: 1, 
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, 
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0, 
  minHeight: 48,
}));

const SectionBody = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
});

const ScrollHiddenBox = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
});

function DashboardPage() {
  const navigate = useNavigate();
  const theme = useTheme();

  // --- State ---
  const [userId, setUserId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [tier, setTier] = useState<number | null>(null);

  const [globalFolders, setGlobalFolders] = useState<FolderRow[]>([]);
  const [orgFolders, setOrgFolders] = useState<FolderRow[]>([]);
  const [myFolders, setMyFolders] = useState<FolderRow[]>([]);
  const [allFolders, setAllFolders] = useState<FolderRow[]>([]);
  
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);

  const [selectedFolder, setSelectedFolder] = useState<FolderRow | null>(null);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [parentForNewFolder, setParentForNewFolder] = useState<FolderRow | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmDesc, setConfirmDesc] = useState<string | undefined>(undefined);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const confirmActionRef = useRef<null | (() => Promise<void>)>(null);

  // --- Auth ---
  const getAuthHeaders = useCallback((): RawAxiosRequestHeaders => {
    const user_id = localStorage.getItem("user_id") || "";
    const access_token = localStorage.getItem("access_token") || "";
    const refresh_token = localStorage.getItem("refresh_token") || "";
    return {
      "x-user-id": user_id,
      "x-access-token": access_token,
      "x-refresh-token": refresh_token,
    };
  }, []);

  // ✅ 권한 체크 로직
  const isTier0 = tier === 0;
  const isGlobalOrg = orgId === '#GLOBAL#';
  const isSuperAdmin = isGlobalOrg && isTier0; // #GLOBAL# 조직의 관리자

  const syncMe = useCallback(async () => {
    try {
      const res = await axios.get<ApiResponse<any>>(`${API_BASE}auth/me`, { headers: getAuthHeaders() });
      if (res.data?.success && res.data.data) {
        const { user_id, org_id, org_name, tier } = res.data.data;
        if (user_id) setUserId(user_id);
        if (org_id) setOrgId(org_id);
        if (org_name) setOrgName(org_name);
        if (typeof tier === "number") setTier(tier);

        localStorage.setItem("user_id", user_id || "");
        localStorage.setItem("org_id", org_id || "");
        localStorage.setItem("tier", String(tier));
      }
    } catch {}
  }, [getAuthHeaders]);

  // --- Data Fetching ---
  const fetchFolders = useCallback(async () => {
    if (!orgId || !userId) return;
    setFoldersError(null);
    setFoldersLoading(true);
    try {
      const res = await axios.get<ApiResponse<FolderRow[]>>(
        `${API_BASE}folders`,
        { params: { org_id: orgId }, headers: getAuthHeaders() }
      );
      if (!res.data.success) throw new Error(res.data.error || "Failed");

      const allRows = res.data.data || [];
      setAllFolders(allRows);

      // [분류 로직 개선]
      // 1. 전체 공용 (#GLOBAL#)
      //    - 슈퍼 관리자(#GLOBAL# 소속)라면, 본인이 만든 폴더가 곧 GLOBAL 폴더임.
      //    - 일반 유저라면, #GLOBAL# 폴더를 그대로 받음.
      const globalList = allRows.filter((f) => f.org_id === '#GLOBAL#');

      let orgList: FolderRow[] = [];
      let myList: FolderRow[] = [];

      if (isSuperAdmin) {
        // ✅ [슈퍼 관리자]
        // 모든 작업은 '전체 공용'에서 이루어짐.
        // '조직 공용', '내 팀'은 비워둠 (화면에서 숨길 예정).
        orgList = [];
        myList = [];
      } else if (isTier0) {
        // ✅ [일반 조직 관리자]
        // 조직 공용 = 내 조직 + 내가 만든 것
        orgList = allRows.filter((f) => f.org_id === orgId && f.created_by === userId);
        myList = [];
      } else {
        // ✅ [팀원]
        // 조직 공용 = 내 조직 + 남(관리자)이 만든 것
        orgList = allRows.filter((f) => f.org_id === orgId && f.created_by !== userId);
        // 내 팀 = 내 조직 + 내가 만든 것
        myList = allRows.filter((f) => f.org_id === orgId && f.created_by === userId);
      }

      setGlobalFolders(globalList);
      setOrgFolders(orgList);
      setMyFolders(myList);

      setSelectedFolder((prev) => {
        if (!prev) return null;
        const found = allRows.find((f) => f.folder_id === prev.folder_id);
        if (!found) { setFiles([]); return null; }
        return found;
      });
    } catch (e: any) {
      setFoldersError(e?.message);
      setGlobalFolders([]); setOrgFolders([]); setMyFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [orgId, userId, getAuthHeaders, isTier0, isSuperAdmin]);

  const fetchFiles = useCallback(async (folder: FolderRow) => {
    setFilesError(null);
    setFilesLoading(true);
    try {
      const res = await axios.get<ApiResponse<FileRow[]>>(
        `${API_BASE}folders/${folder.folder_id}/files`,
        { headers: getAuthHeaders() }
      );
      if (!res.data.success) throw new Error(res.data.error);
      setFiles(res.data.data || []);
    } catch (e: any) {
      setFilesError(e?.message);
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [getAuthHeaders]);

  // --- Handlers ---
  const onSelectFolder = (folder: FolderRow) => {
    setSelectedFolder(folder);
    fetchFiles(folder);
  };

  const handleCreateRootFolder = () => {
    setParentForNewFolder(null);
    setCreateOpen(true);
  };

  const handleCreateChildFolder = (parent: FolderRow) => {
    if (parent.file_count > 0) {
      alert("파일이 업로드된 폴더에는 하위 폴더를 만들 수 없습니다.\n(최하위 폴더 규칙)");
      return;
    }
    const siblings = allFolders.filter(f => f.parent_folder_id === parent.parent_folder_id);
    const hasLeafSibling = siblings.some(s => s.file_count > 0);
    if (hasLeafSibling) {
      alert("형제 폴더 중 파일이 포함된 폴더가 있어, 구조상 하위 폴더를 만들 수 없습니다.\n(Leaf Node 그룹)");
      return;
    }
    setParentForNewFolder(parent);
    setCreateOpen(true);
  };

  const handleRequestDeleteFolder = (folder: FolderRow) => {
    // 삭제 권한: 본인 생성 OR (조직관리자 && 내 조직) OR (슈퍼관리자 && GLOBAL)
    const isMine = folder.created_by === userId;
    const canDelete = isMine || (isTier0 && folder.org_id === orgId) || (isSuperAdmin && folder.org_id === '#GLOBAL#');
    
    if (!canDelete) return alert("권한이 없습니다.");

    openConfirm("폴더 삭제", `"${folder.name}" 폴더를 삭제하시겠습니까?`, async () => {
      await axios.delete(`${API_BASE}folders/${folder.folder_id}`, { headers: getAuthHeaders() });
      await fetchFolders();
      if (selectedFolder?.folder_id === folder.folder_id) {
        setSelectedFolder(null); setFiles([]);
      }
    });
  };

  const handleRequestDeleteFile = (file: FileRow) => {
    const isMine = file.uploader_id === userId;
    const canDelete = isMine || (isTier0 && file.org_id === orgId) || (isSuperAdmin && file.org_id === '#GLOBAL#');
    if (!canDelete) return alert("권한이 없습니다.");

    openConfirm("파일 삭제", `v${file.version_no} 파일을 삭제하시겠습니까?`, async () => {
      await axios.delete(`${API_BASE}files/${file.file_id}`, { headers: getAuthHeaders() });
      setFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
      fetchFolders(); 
    });
  };

  const openConfirm = (title: string, desc: string, action: () => Promise<void>) => {
    setConfirmTitle(title); setConfirmDesc(desc); confirmActionRef.current = action; setConfirmOpen(true);
  };

  const handleConfirmOK = async () => {
    if (!confirmActionRef.current) return;
    setConfirmLoading(true);
    try { await confirmActionRef.current(); setConfirmOpen(false); }
    catch (e) { alert("Error"); }
    finally { setConfirmLoading(false); }
  };

  const handleLogout = () => { localStorage.clear(); navigate("/login"); };

  useEffect(() => { syncMe(); }, [syncMe]);
  useEffect(() => { if (userId && orgId) fetchFolders(); }, [userId, orgId, fetchFolders]);

  // --- 권한 및 상태 계산 ---
  const isLeafFolder = useMemo(() => {
    if (!selectedFolder) return false;
    return !allFolders.some(f => f.parent_folder_id === selectedFolder.folder_id);
  }, [selectedFolder, allFolders]);

  // ✅ [수정] 편집/업로드 권한 로직
  // 1. GLOBAL 폴더이고 && 슈퍼 관리자면 -> 수정 가능
  // 2. 내 조직 폴더이고 && (내 거거나 OR 관리자면) -> 수정 가능
  const isGlobalFolder = selectedFolder?.org_id === '#GLOBAL#';
  
  let canEditMeta = false;
  if (selectedFolder) {
    if (isGlobalFolder) {
      // GLOBAL 폴더는 슈퍼 관리자만 수정 가능
      canEditMeta = isSuperAdmin;
    } else {
      // 일반 조직 폴더
      const isMySelected = selectedFolder.created_by === userId;
      const isOrgSelectedAndAdmin = isTier0 && selectedFolder.org_id === orgId;
      canEditMeta = isMySelected || isOrgSelectedAndAdmin;
    }
  }
  
  const hasSubFolders = !!selectedFolder?.has_child;
  const canUpload = canEditMeta && !hasSubFolders;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, boxSizing: 'border-box', overflow: 'hidden', backgroundColor: theme.palette.background.default }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Data Lake Dashboard</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {tier !== null && <Chip label={isTier0 ? "관리자" : "팀원"} size="small" variant="outlined" color={isTier0 ? "secondary" : "default"} />}
        {orgId && <Chip label={`Org: ${orgName ?? orgId}`} size="small" color="primary" />}
        <Button variant="outlined" onClick={() => navigate("/search-bundles")}>검색 번들 관리</Button>
        <Button variant="outlined" onClick={handleLogout}>로그아웃</Button>
      </Stack>

      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
        {/* Left Panel */}
        <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
          <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>
            
            {/* A. 전체 공용 */}
            <SectionBox>
              <SectionHeader>
                <PublicIcon fontSize="small" color={isSuperAdmin ? "primary" : "disabled"} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isSuperAdmin ? 'primary.main' : 'text.secondary' }}>전체 공용</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {/* ✅ [수정] 슈퍼 관리자라면 전체 공용에 + 버튼 활성화 */}
                {isSuperAdmin && (
                  <Tooltip title="새 최상위 폴더 만들기">
                    <IconButton size="small" onClick={handleCreateRootFolder}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </SectionHeader>
              <SectionBody>
                <FolderTree
                  folders={globalFolders} loading={foldersLoading} error={null}
                  selectedFolderId={selectedFolder?.folder_id ?? null} onSelectFolder={onSelectFolder}
                  readOnly={!isSuperAdmin} // 슈퍼 관리자만 편집 가능
                  onClickCreateChild={handleCreateChildFolder} onRequestDeleteFolder={handleRequestDeleteFolder}
                />
                {globalFolders.length === 0 && !foldersLoading && <Typography variant="caption" sx={{ pl: 4, pt: 1, display: 'block', color: 'text.disabled' }}>(비어 있음)</Typography>}
              </SectionBody>
            </SectionBox>

            {/* B. 조직 공용 (슈퍼 관리자는 볼 필요 없음 - 본인이 관리하는게 전체공용이므로) */}
            {!isSuperAdmin && (
              <SectionBox>
                <SectionHeader>
                  <DomainIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>조직 공용</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {/* 일반 조직 관리자만 + 버튼 */}
                  {isTier0 && (
                    <Tooltip title="새 최상위 폴더 만들기">
                      <IconButton size="small" onClick={handleCreateRootFolder}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </SectionHeader>
                <SectionBody>
                  <FolderTree
                    folders={orgFolders} loading={foldersLoading} error={null}
                    selectedFolderId={selectedFolder?.folder_id ?? null} onSelectFolder={onSelectFolder}
                    readOnly={!isTier0}
                    onClickCreateChild={handleCreateChildFolder} onRequestDeleteFolder={handleRequestDeleteFolder}
                  />
                  {orgFolders.length === 0 && !foldersLoading && <Typography variant="caption" sx={{ pl: 4, pt: 1, display: 'block', color: 'text.disabled' }}>(비어 있음)</Typography>}
                </SectionBody>
              </SectionBox>
            )}

            {/* C. 내 팀 (관리자가 아닐 때만) */}
            {!isTier0 && (
              <SectionBox>
                <SectionHeader>
                  <PersonIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>내 팀</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="새 최상위 폴더 만들기">
                    <IconButton size="small" onClick={handleCreateRootFolder}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </SectionHeader>
                <SectionBody>
                  <FolderTree
                    folders={myFolders} loading={foldersLoading} error={foldersError}
                    selectedFolderId={selectedFolder?.folder_id ?? null} onSelectFolder={onSelectFolder}
                    readOnly={false}
                    onClickCreateChild={handleCreateChildFolder} onRequestDeleteFolder={handleRequestDeleteFolder}
                  />
                  {myFolders.length === 0 && !foldersLoading && (
                    <Box sx={{ p: 2, textAlign: 'center', opacity: 0.5 }}>
                      <Typography variant="caption">생성된 폴더가 없습니다.</Typography>
                    </Box>
                  )}
                </SectionBody>
              </SectionBox>
            )}
          </Paper>
        </Grid>

        {/* Right Panel */}
        <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
          <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedFolder ? (
              <>
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedFolder.name}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {canEditMeta && <Button onClick={() => setEditOpen(true)} size="small">정보 수정</Button>}
                    {canEditMeta && (
                      <Button 
                        variant="contained" size="small" startIcon={<DescriptionIcon />} 
                        onClick={() => setUploadOpen(true)}
                        disabled={!canUpload} 
                        color={!canUpload ? "inherit" : "primary"}
                      >
                        파일 업로드
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{selectedFolder.description || "설명 없음"}</Typography>
                  {canEditMeta && hasSubFolders && (
                    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ py: 0, alignItems: 'center' }}>
                      <Typography variant="caption">이 폴더는 하위 폴더를 가지고 있어 파일을 업로드할 수 없습니다. (Branch Node)</Typography>
                    </Alert>
                  )}
                </Box>

                <ScrollHiddenBox sx={{ p: 2 }}>
                  {filesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                  ) : filesError ? (
                    <Alert severity="error">{filesError}</Alert>
                  ) : files.length > 0 ? (
                    <Stack spacing={0}>
                      {files.map((f) => (
                        <Box key={f.file_id}>
                          <FileItemCard file={f} onDelete={canEditMeta ? handleRequestDeleteFile : undefined} />
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, opacity: 0.6 }}>
                      <DescriptionIcon sx={{ fontSize: 48, mb: 1, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">업로드된 파일이 없습니다.</Typography>
                    </Box>
                  )}
                </ScrollHiddenBox>
              </>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                <DescriptionIcon sx={{ fontSize: 64, mb: 2, opacity: 0.1 }} />
                <Typography variant="h6" color="text.secondary">폴더를 선택해주세요</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <FolderCreateDialog open={createOpen} parentFolderId={parentForNewFolder?.folder_id ?? null} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchFolders(); }} />
      {selectedFolder && canEditMeta && (
        <>
          <FolderEditDialog open={editOpen} folder={selectedFolder} onClose={() => setEditOpen(false)} onUpdated={() => { setEditOpen(false); fetchFolders(); }} />
          {canUpload && (
            <FileUploadDialog open={uploadOpen} folderId={selectedFolder.folder_id} onClose={() => setUploadOpen(false)} onUploaded={() => { setUploadOpen(false); fetchFiles(selectedFolder); }} />
          )}
        </>
      )}
      <ConfirmDialog open={confirmOpen} title={confirmTitle} description={confirmDesc} loading={confirmLoading} onConfirm={handleConfirmOK} onClose={() => setConfirmOpen(false)} />
    </Box>
  );
}

export default DashboardPage;